import { Buffer } from 'buffer';
import { WebSessionManager as SessionManager, createSessionWithSignature, genAuthNonce, genSolanaMessage, buildSetDocumentsTransaction, convertRemainingAccounts } from '@pooflabs/core';
import { setCurrentUser, setAuthLoading } from '../../global';
import { getPlatform, detectAndroid } from '../../platform';
import { Connection, PublicKey, Transaction, VersionedMessage, VersionedTransaction } from '@solana/web3.js';
import * as anchor from '@coral-xyz/anchor';
import bs58 from 'bs58';
import { SOLANA_DEVNET_RPC_URL, SOLANA_MAINNET_RPC_URL, SURFNET_RPC_URL } from '..';
import { confirmAndCheckTransaction } from './transaction-utils';
/**
 * Storage key for persisting the MWA auth_token across cold starts.
 * Holds JSON `{ token: string, address: string }`. Cleared by
 * `logout()`. Hydrated by the constructor on native so cold-start
 * doesn't force the user back into Phantom's approval flow when the
 * underlying wallet authorization is still valid.
 */
const MWA_AUTH_TOKEN_STORAGE_KEY = 'tarobase_mwa_auth_token';
/**
 * Detects whether the current environment is a mobile browser capable of
 * Mobile Wallet Adapter (MWA) communication.
 *
 * Returns true on Android browsers (Chrome, etc.) where MWA intents work,
 * or inside a Seeker/Saga in-app browser context.
 */
export function isMobileWalletAvailable() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined')
        return false;
    return detectAndroid();
}
const ED25519_SIGNATURE_LENGTH = 64;
const STORED_AUTH_METHOD_KEY = 'tarobase_last_auth_method';
const MWA_AUTH_METHOD = 'mobile-wallet-adapter';
function findMwaError(e, maxDepth = 5) {
    var _a;
    let cur = e;
    let depth = 0;
    while (cur && depth < maxDepth) {
        if ((cur === null || cur === void 0 ? void 0 : cur.name) === 'SolanaMobileWalletAdapterError' && typeof (cur === null || cur === void 0 ? void 0 : cur.code) === 'string') {
            return { code: cur.code, message: String((_a = cur.message) !== null && _a !== void 0 ? _a : '') };
        }
        cur = cur === null || cur === void 0 ? void 0 : cur.cause;
        depth++;
    }
    return null;
}
/**
 * Returns the matched MwaErrorInfo when the error is from an association
 * failure that an automatic retry can recover from (i.e. the wallet exists
 * on-device but the dApp's protocol session didn't complete in time). Returns
 * null for non-retryable errors (user cancel, unknown wallet errors, etc.).
 *
 * Specifically:
 *   - ERROR_WALLET_NOT_FOUND: thrown from the protocol's startScenario when
 *     it gave up waiting for a wallet to respond. On Seeker this races the
 *     first-time-consent UI on the very first authorize.
 *   - ERROR_SESSION_TIMEOUT: similar — the protocol's session-establishment
 *     timer expired.
 *   - ERROR_ASSOCIATION_CANCELLED: shared by user-cancel ("Wallet connection
 *     cancelled by user") AND the 30s wallet-standard WALLET_ASSOCIATION_TIMEOUT
 *     ("Wallet connection timed out"). Discriminate by message — never retry
 *     a user cancel.
 */
function isMwaAssociationRetryable(e) {
    const info = findMwaError(e);
    if (!info)
        return null;
    if (info.code === 'ERROR_WALLET_NOT_FOUND')
        return info;
    if (info.code === 'ERROR_SESSION_TIMEOUT')
        return info;
    if (info.code === 'ERROR_ASSOCIATION_CANCELLED' && /timed out/i.test(info.message))
        return info;
    return null;
}
/**
 * Single-retry wrapper for MWA association failures. Used ONLY for login's
 * `standard:connect` and `solana:signMessage` calls — never for
 * `signAndSendTransaction`/`runTransaction` because retry on a path that
 * may have already submitted a tx can double-send.
 *
 * The retry exists for one specific scenario: on first-time MWA usage from a
 * given origin on Seeker, the protocol's session-establishment timer races
 * Seeker's first-time consent UI. The user reads the consent sheet, taps
 * Connect — but by then the protocol has given up and thrown
 * ERROR_WALLET_NOT_FOUND. The consent itself IS persisted on Seeker though,
 * so a second attempt skips the consent sheet, jumps straight to
 * Verify/Approve, and completes inside the timeout.
 */
async function withMwaAssociationRetry(fn) {
    try {
        return await fn();
    }
    catch (e) {
        const retryable = isMwaAssociationRetryable(e);
        if (!retryable)
            throw e;
        // Seeker's consent sheet is a bottom-sheet overlay — Chrome doesn't
        // reliably flip document.visibilityState to 'hidden' while it's open,
        // so visibility-based waits fire too early. Use a fixed delay long
        // enough for Seeker to dismiss its sheet and the OS to release intent
        // state. 2.5s is empirically safe; tune from device traces if needed.
        await new Promise(r => setTimeout(r, 2500));
        try {
            return await fn();
        }
        catch (e2) {
            // Only mask the error with the clean Seeker message when the
            // second failure is also an association-flavored error. If it's
            // a user cancel, wallet rejection, or unknown error, rethrow
            // the original so the host app surfaces the real cause.
            if (isMwaAssociationRetryable(e2)) {
                throw new Error("Couldn't connect to your Seeker wallet. Please try again.");
            }
            throw e2;
        }
    }
}
/**
 * User-gesture-mediated wallet-standard sign helper.
 *
 * Why this exists: login flows that go through wallet-standard call two
 * separate transacts (standard:connect, then solana:signMessage). Each
 * transact dispatches its own `solana-wallet:` Android intent navigation
 * via `window.location.assign(associationUrl)` (see
 * @solana-mobile/mobile-wallet-adapter-protocol/lib/cjs/index.browser.js
 * `launchAssociation()` at line 459). Chrome requires **transient user
 * activation** for each custom-scheme navigation. The first transact
 * consumes the activation from the user's "Continue to Allow" tap on the
 * LNA modal; by the time the second transact (signMessage) runs as a JS
 * continuation, the activation has decayed, Chrome blocks the navigation,
 * no `blur` event fires, the protocol's `getDetectionPromise` (3000ms
 * timeout, line 433) rejects, and we get `ERROR_WALLET_NOT_FOUND`.
 *
 * Fix: show a Tarobase-controlled modal between the two transacts. When the
 * user taps the modal's "Sign in" button, that tap IS a fresh user
 * activation. We invoke the sign function directly inside the click handler
 * — no `await` between the tap and the call — so the activation propagates
 * to the protocol's `location.assign`.
 *
 * Failure recovery is also user-gesture driven: if the sign function fails
 * with a retryable association error, we re-enable the button so the user's
 * next tap is a new activation (a real retry). A timer-based retry would
 * just hit the same Chrome block. Cap at 3 attempts before giving up.
 */
async function awaitSignInGestureAndSign(theme, signFn, noResultError = 'MWA returned no signature') {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        // SSR / non-browser: skip the gesture, just call (the activation
        // model doesn't apply outside the browser).
        const results = await signFn();
        if (!results || results.length === 0)
            throw new Error(noResultError);
        return results[0];
    }
    return new Promise((resolve, reject) => {
        const isDark = theme === 'dark'
            || (theme == null
                && typeof window.matchMedia === 'function'
                && window.matchMedia('(prefers-color-scheme: dark)').matches);
        // Palette
        const overlayBg = 'rgba(0, 0, 0, 0.62)';
        const cardBg = isDark ? '#1a1a1f' : '#ffffff';
        const titleColor = isDark ? '#ffffff' : '#0a0a0a';
        const subtitleColor = isDark ? '#a1a1aa' : '#52525b';
        const btnBg = isDark ? '#ffffff' : '#0a0a0a';
        const btnText = isDark ? '#0a0a0a' : '#ffffff';
        const btnDisabledBg = isDark ? '#3f3f46' : '#d4d4d8';
        const btnDisabledText = isDark ? '#71717a' : '#71717a';
        const errorColor = isDark ? '#fca5a5' : '#dc2626';
        const closeColor = isDark ? '#71717a' : '#a1a1aa';
        const fontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
        const overlay = document.createElement('div');
        overlay.setAttribute('data-tarobase-mwa-gesture', 'true');
        overlay.style.cssText = [
            'position: fixed',
            'inset: 0',
            'z-index: 2147483646',
            `background: ${overlayBg}`,
            'display: flex',
            'align-items: center',
            'justify-content: center',
            'padding: 20px',
            'box-sizing: border-box',
            `font-family: ${fontStack}`,
        ].join('; ');
        const card = document.createElement('div');
        card.style.cssText = [
            `background: ${cardBg}`,
            'border-radius: 20px',
            'padding: 28px 24px 24px',
            'max-width: 340px',
            'width: 100%',
            'box-shadow: 0 20px 50px rgba(0, 0, 0, 0.28)',
            'position: relative',
            'box-sizing: border-box',
        ].join('; ');
        const closeBtn = document.createElement('button');
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '&times;';
        closeBtn.style.cssText = [
            'position: absolute',
            'top: 12px',
            'right: 12px',
            'background: transparent',
            'border: none',
            `color: ${closeColor}`,
            'font-size: 24px',
            'line-height: 1',
            'cursor: pointer',
            'padding: 6px 10px',
            'border-radius: 8px',
        ].join('; ');
        const title = document.createElement('div');
        title.textContent = 'Almost there';
        title.style.cssText = [
            `color: ${titleColor}`,
            'font-size: 20px',
            'font-weight: 600',
            'margin-bottom: 6px',
            'text-align: center',
        ].join('; ');
        const subtitle = document.createElement('div');
        subtitle.textContent = 'Tap to sign in with your wallet';
        subtitle.style.cssText = [
            `color: ${subtitleColor}`,
            'font-size: 14px',
            'line-height: 1.4',
            'margin-bottom: 22px',
            'text-align: center',
        ].join('; ');
        const errorRow = document.createElement('div');
        errorRow.style.cssText = [
            `color: ${errorColor}`,
            'font-size: 13px',
            'line-height: 1.4',
            'margin-bottom: 14px',
            'text-align: center',
            'min-height: 0',
            'transition: min-height 100ms ease',
        ].join('; ');
        const button = document.createElement('button');
        button.textContent = 'Sign in';
        const setButtonEnabledStyle = () => {
            button.style.cssText = [
                `background: ${btnBg}`,
                `color: ${btnText}`,
                'border: none',
                'border-radius: 14px',
                'padding: 14px 20px',
                'font-size: 16px',
                'font-weight: 600',
                'width: 100%',
                'cursor: pointer',
                '-webkit-tap-highlight-color: transparent',
                'box-sizing: border-box',
                `font-family: ${fontStack}`,
            ].join('; ');
        };
        const setButtonDisabledStyle = () => {
            button.style.cssText = [
                `background: ${btnDisabledBg}`,
                `color: ${btnDisabledText}`,
                'border: none',
                'border-radius: 14px',
                'padding: 14px 20px',
                'font-size: 16px',
                'font-weight: 600',
                'width: 100%',
                'cursor: default',
                '-webkit-tap-highlight-color: transparent',
                'box-sizing: border-box',
                `font-family: ${fontStack}`,
            ].join('; ');
        };
        setButtonEnabledStyle();
        card.appendChild(closeBtn);
        card.appendChild(title);
        card.appendChild(subtitle);
        card.appendChild(errorRow);
        card.appendChild(button);
        overlay.appendChild(card);
        document.body.appendChild(overlay);
        let settled = false;
        let attemptCount = 0;
        const MAX_ATTEMPTS = 3;
        const cleanup = () => {
            try {
                overlay.remove();
            }
            catch ( /* ignore */_a) { /* ignore */ }
            document.removeEventListener('keydown', onKeydown);
        };
        const finishResolve = (value) => {
            if (settled)
                return;
            settled = true;
            cleanup();
            resolve(value);
        };
        const finishReject = (err) => {
            if (settled)
                return;
            settled = true;
            cleanup();
            reject(err);
        };
        const handleCancel = () => {
            // Phrasing matches login()'s isUserRejection substring check so
            // the existing catch suppresses error-log noise.
            finishReject(new Error('User cancelled wallet sign in'));
        };
        closeBtn.addEventListener('click', () => {
            handleCancel();
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                handleCancel();
            }
        });
        const onKeydown = (e) => {
            if (e.key === 'Escape') {
                handleCancel();
            }
        };
        document.addEventListener('keydown', onKeydown);
        button.addEventListener('click', () => {
            if (settled)
                return;
            attemptCount++;
            const attempt = attemptCount;
            // Disable to prevent double-clicks while the wallet popup is up.
            button.disabled = true;
            setButtonDisabledStyle();
            const originalText = 'Sign in';
            button.textContent = 'Signing in…';
            errorRow.textContent = '';
            errorRow.style.minHeight = '0';
            // CRITICAL: invoke signFn() synchronously inside the click handler
            // so Chrome's transient user activation propagates to the
            // protocol's window.location.assign(associationUrl) inside
            // launchAssociation(). Do not `await` before calling.
            signFn().then((results) => {
                if (!results || results.length === 0) {
                    finishReject(new Error(noResultError));
                    return;
                }
                finishResolve(results[0]);
            }).catch((err) => {
                const retryable = isMwaAssociationRetryable(err);
                if (retryable && attempt < MAX_ATTEMPTS) {
                    // Re-enable the button so the user's next tap provides a
                    // fresh user activation. Show inline error text.
                    button.disabled = false;
                    setButtonEnabledStyle();
                    button.textContent = originalText;
                    errorRow.textContent = "Couldn't connect — tap to try again";
                    errorRow.style.minHeight = '20px';
                    return;
                }
                if (retryable) {
                    // Hit the attempt cap with a still-retryable failure.
                    finishReject(new Error("Couldn't connect to your Seeker wallet. Please try again."));
                    return;
                }
                // Non-retryable error (user cancel inside Seeker, wallet
                // rejected sign, unknown error, etc.) — surface as-is.
                finishReject(err);
            });
        });
    });
}
/**
 * Sign through the gesture modal ONLY when transient user activation has
 * already decayed. Fast transactions still hold the activation from the
 * user's tap, so the `solana-wallet:` navigation succeeds directly with no
 * modal (preserves prior UX — no extra tap). Slow transactions — e.g. a swap
 * whose server-side build exceeds Chrome's ~5s activation window — have lost
 * it, so we fall back to awaitSignInGestureAndSign to mint a fresh activation
 * via a "Sign in" tap. Returns the first result (same shape as the bare
 * wallet-standard feature call and as awaitSignInGestureAndSign).
 */
async function signWithFreshActivation(theme, signFn, noResultError = 'MWA returned no signature') {
    var _a;
    const activationLive = typeof navigator !== 'undefined' &&
        ((_a = navigator.userActivation) === null || _a === void 0 ? void 0 : _a.isActive) === true;
    if (activationLive) {
        // Live gesture: invoke synchronously (no await before signFn) so the
        // activation propagates to the protocol's location.assign navigation.
        const results = await signFn();
        if (!results || results.length === 0)
            throw new Error(noResultError);
        return results[0];
    }
    // Activation gone: surface the modal so the user's tap mints a fresh one.
    return awaitSignInGestureAndSign(theme, signFn, noResultError);
}
/**
 * Normalize a chain string to a wallet-standard Solana chain identifier.
 *
 * Handles three input shapes that all show up in practice:
 *  - Bare cluster name from `SolanaMobileWalletConfig.cluster` (e.g. 'devnet').
 *  - Prefixed `solana:cluster` (e.g. 'solana:devnet').
 *  - Double-prefixed `solana:solana:cluster` (constructor used to wrap cluster
 *    with `solana:` blindly; if a caller already passed a prefixed string the
 *    result was `solana:solana:devnet`).
 *
 * Wallet-standard Solana chains are exactly:
 *   'solana:mainnet' | 'solana:devnet' | 'solana:testnet' | 'solana:localnet'
 * — `mainnet-beta` is NOT a valid wallet-standard identifier and must be
 * normalized to `solana:mainnet`.
 */
function mapChainToWalletStandard(input) {
    if (!input)
        return 'solana:mainnet';
    let s = String(input).toLowerCase();
    while (s.startsWith('solana:'))
        s = s.slice('solana:'.length);
    if (s === 'mainnet-beta' || s === 'mainnet')
        return 'solana:mainnet';
    if (s === 'devnet')
        return 'solana:devnet';
    if (s === 'testnet')
        return 'solana:testnet';
    if (s === 'localnet')
        return 'solana:localnet';
    return 'solana:mainnet';
}
/**
 * Serialize a Transaction/VersionedTransaction to a Uint8Array wire payload
 * that the wallet-standard `solana:signTransaction` /
 * `solana:signAndSendTransaction` features expect.
 *
 * Legacy transactions must serialize with `requireAllSignatures: false` and
 * `verifySignatures: false` — the wallet hasn't signed yet, and the default
 * `serialize()` would throw "Signature verification failed". Versioned
 * transactions use the default `serialize()` which already permits unsigned
 * payloads. Mirrors the helper at
 * `@solana-mobile/mobile-wallet-adapter-protocol-web3js/lib/esm/index.browser.js:13-18`.
 */
function txToWireBytes(tx) {
    if ('version' in tx)
        return tx.serialize();
    return tx.serialize({ requireAllSignatures: false, verifySignatures: false });
}
/**
 * Deserialize a wire-format transaction returned by the wallet
 * `solana:signTransaction` feature. Mirrors the helper at
 * `@solana-mobile/mobile-wallet-adapter-protocol-web3js/lib/esm/index.browser.js:19-23`
 * so legacy and versioned bytes both round-trip correctly.
 */
function txFromWireBytes(byteArray) {
    const messageOffset = byteArray[0] * ED25519_SIGNATURE_LENGTH + 1;
    const messageVersion = VersionedMessage.deserializeMessageVersion(byteArray.slice(messageOffset, byteArray.length));
    if (messageVersion === 'legacy') {
        return Transaction.from(byteArray);
    }
    return VersionedTransaction.deserialize(byteArray);
}
/**
 * Per-method runtime narrowing of wallet-standard features. The wallet's
 * `.features` type is a union (`signAndSendTransaction` | `signTransaction`),
 * and after authorization the wallet may also narrow `#optionalFeatures`
 * based on actual wallet capabilities. Narrow at the call site so a method
 * that needs only `signMessage` doesn't fail when a wallet lacks
 * `signTransaction`.
 */
function getSignMessageFeature(wallet) {
    const f = wallet.features;
    const feat = f['solana:signMessage'];
    if (!feat || typeof feat.signMessage !== 'function') {
        throw new Error('Wallet does not support solana:signMessage');
    }
    return feat;
}
function getSignTransactionFeature(wallet) {
    const f = wallet.features;
    const feat = f['solana:signTransaction'];
    if (!feat || typeof feat.signTransaction !== 'function') {
        throw new Error('Wallet does not support solana:signTransaction');
    }
    return feat;
}
function getSignAndSendTransactionFeature(wallet) {
    const f = wallet.features;
    const feat = f['solana:signAndSendTransaction'];
    if (!feat || typeof feat.signAndSendTransaction !== 'function') {
        throw new Error('Wallet does not support solana:signAndSendTransaction');
    }
    return feat;
}
function getConnectFeature(wallet) {
    const f = wallet.features;
    const feat = f['standard:connect'];
    if (!feat || typeof feat.connect !== 'function') {
        throw new Error('Wallet does not support standard:connect');
    }
    return feat;
}
function getDisconnectFeature(wallet) {
    const f = wallet.features;
    const feat = f['standard:disconnect'];
    return feat && typeof feat.disconnect === 'function' ? feat : null;
}
function writeAuthMethod(method) {
    try {
        if (method === null) {
            getPlatform().storage.removeItem(STORED_AUTH_METHOD_KEY);
        }
        else {
            getPlatform().storage.setItem(STORED_AUTH_METHOD_KEY, method);
        }
    }
    catch (_a) {
        // storage may be unavailable
    }
}
function readAuthMethod() {
    try {
        return getPlatform().storage.getItem(STORED_AUTH_METHOD_KEY);
    }
    catch (_a) {
        return null;
    }
}
/**
 * Registers Mobile Wallet Adapter as a wallet-standard wallet so it appears
 * in wallet selection UIs and can be discovered by other wallet-standard consumers.
 *
 * Call this once at app startup (in a browser-only / non-SSR context).
 *
 * @param config - App identity and optional remote host authority for desktop QR code support
 */
export async function registerMobileWalletAdapter(config) {
    var _a, _b;
    if (typeof window === 'undefined')
        return;
    try {
        const walletStandardMobile = await import('@solana-mobile/wallet-standard-mobile');
        const registerMwa = walletStandardMobile.registerMwa || ((_a = walletStandardMobile.default) === null || _a === void 0 ? void 0 : _a.registerMwa);
        if (!registerMwa) {
            console.warn('[SolanaMobileWallet] registerMwa not found in @solana-mobile/wallet-standard-mobile');
            return;
        }
        const options = {};
        if (config === null || config === void 0 ? void 0 : config.appIdentity) {
            options.appIdentity = config.appIdentity;
        }
        if (config === null || config === void 0 ? void 0 : config.chains) {
            options.chains = config.chains;
        }
        if (config === null || config === void 0 ? void 0 : config.remoteHostAuthority) {
            options.remoteHostAuthority = config.remoteHostAuthority;
        }
        // Use the library's default helpers if available
        if (walletStandardMobile.createDefaultAuthorizationCache) {
            options.authorizationCache = walletStandardMobile.createDefaultAuthorizationCache();
        }
        if (walletStandardMobile.createDefaultChainSelector) {
            options.chainSelector = walletStandardMobile.createDefaultChainSelector();
        }
        // Default: suppress the "We can't find a wallet" modal — see
        // SolanaMobileWalletProvider.ensureWallet() for why. Consumers that
        // want their own UX can pass config.onWalletNotFound.
        options.onWalletNotFound = (_b = config === null || config === void 0 ? void 0 : config.onWalletNotFound) !== null && _b !== void 0 ? _b : (async () => {
            // intentional no-op
        });
        registerMwa(options);
    }
    catch (e) {
        console.debug('[SolanaMobileWallet] @solana-mobile/wallet-standard-mobile not available, skipping wallet-standard registration');
    }
}
/**
 * SolanaMobileWalletProvider implements the AuthProvider interface using
 * Solana Mobile's wallet-standard wrapper (`LocalSolanaMobileWalletAdapterWallet`).
 *
 * Why wallet-standard and not the raw MWA protocol: as of
 * `@solana-mobile/wallet-standard-mobile@0.5.0+`, the wallet's internal
 * `#transact` calls `checkLocalNetworkAccessPermission()` before opening the
 * localhost WebSocket. That helper renders a three-stage UX flow Solana
 * Mobile designed specifically to defuse Chrome's Local Network Access
 * permission dialog (which renders with disabled buttons on Android Chrome,
 * the source of the long-running Seeker MWA bug):
 *
 *   1. "Allow connections to your wallet" informational modal.
 *   2. Chrome's permission prompt (interactive because it's spawned as a
 *      fresh user gesture from step 1).
 *   3. "Ready to connect!" success modal.
 *
 * `checkLocalNetworkAccessPermission` is internal to the library — it's not
 * exported. The only way to invoke it is to go through the wallet's
 * `standard:connect` / `solana:sign*` features, which is what this provider
 * does.
 *
 * Notes on UX: login uses two wallet popups in succession — `standard:connect`
 * (authorize) and `solana:signMessage` (sign the Tarobase nonce). The
 * previous implementation combined both inside a single `transact` callback;
 * splitting them is the cost of going through wallet-standard, and is
 * acceptable given the alternative was broken on Seeker.
 */
export class SolanaMobileWalletProvider {
    constructor(networkUrl = null, config = {}) {
        /** LocalSolanaMobileWalletAdapterWallet, lazy-constructed in ensureWallet(). */
        this.wallet = null;
        /**
         * Cached MWA auth_token returned by `wallet.authorize()`. Reused by
         * subsequent native transact() calls (signMessage / signTransaction)
         * so the user doesn't get the wallet-pick popup on every operation.
         * Persisted to platform storage and hydrated by the constructor on
         * native; cleared by logout() and on `ERROR_REAUTHORIZE`.
         */
        this.nativeAuthToken = null;
        /** Cached public key string for the connected MWA wallet (native). */
        this.nativeAddress = null;
        this.networkUrl = networkUrl;
        this.config = config;
        // Allow construction in true React Native environments: getPlatform().hasDOM
        // is false there even though `typeof window === 'object'` (RN exposes a
        // partial window global). Web/PWA paths still keep their original guard:
        // they need real window+document for wallet-standard-mobile's deep-link
        // mechanism, so missing-window in those builds remains a fail-fast.
        const hasDOM = getPlatform().hasDOM;
        if (!hasDOM && typeof window === 'undefined') {
            throw new Error('SolanaMobileWalletProvider can only be instantiated in a browser or React Native environment');
        }
        if (hasDOM && typeof window === 'undefined') {
            throw new Error('SolanaMobileWalletProvider can only be instantiated in a browser environment');
        }
        if (SolanaMobileWalletProvider.instance) {
            return SolanaMobileWalletProvider.instance;
        }
        // `appIdentity.uri` is what Phantom Android (and other MWA wallets)
        // display in their approval modal. On web/PWA this is the page
        // origin; on native there's no `window.location`, so fall back to
        // a stable identifier the user can recognize. Apps can override
        // entirely via `config.appIdentity.uri`.
        const fallbackUri = hasDOM
            ? getPlatform().getLocationOrigin()
            : 'tarobase://app';
        this.appIdentity = config.appIdentity || {
            name: 'TaroBase App',
            uri: fallbackUri,
        };
        this.cluster = config.cluster || 'mainnet-beta';
        // Hydrate cached MWA auth_token from storage on native so we can
        // skip the wallet-pick popup after a cold-start when the underlying
        // wallet authorization is still valid. Sync storage call per the
        // PlatformAdapter contract; safe to swallow read errors (a fresh
        // login() just re-authorizes).
        if (!hasDOM) {
            try {
                const raw = getPlatform().storage.getItem(MWA_AUTH_TOKEN_STORAGE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (typeof parsed.token === 'string' && typeof parsed.address === 'string') {
                        this.nativeAuthToken = parsed.token;
                        this.nativeAddress = parsed.address;
                    }
                }
            }
            catch (_a) {
                // Corrupt or unreadable cache — ignore. login() will re-auth.
            }
        }
        SolanaMobileWalletProvider.instance = this;
    }
    static getInstance(networkUrl, config) {
        if (!SolanaMobileWalletProvider.instance) {
            new SolanaMobileWalletProvider(networkUrl, config);
        }
        return SolanaMobileWalletProvider.instance;
    }
    /** Lazy-construct LocalSolanaMobileWalletAdapterWallet on first need. */
    async ensureWallet() {
        if (this.wallet)
            return this.wallet;
        const mod = await import('@solana-mobile/wallet-standard-mobile');
        const chain = mapChainToWalletStandard(this.cluster);
        this.wallet = new mod.LocalSolanaMobileWalletAdapterWallet({
            appIdentity: this.appIdentity,
            authorizationCache: mod.createDefaultAuthorizationCache(),
            chains: [chain],
            chainSelector: mod.createDefaultChainSelector(),
            // Suppress the default "We can't find a wallet" modal — that
            // modal is misleading when ERROR_WALLET_NOT_FOUND fires during
            // a first-time-consent race on Seeker (the wallet IS installed,
            // the protocol just gave up before the user finished consenting).
            // login()'s withMwaAssociationRetry handles this case; on second
            // failure it surfaces a clean error message to the host app.
            onWalletNotFound: async () => {
                // intentional no-op
            },
        });
        return this.wallet;
    }
    /**
     * Ensure the wallet has an active authorization. After a Tarobase session
     * is restored from storage on cold-start, `wallet.accounts` is empty until
     * we silently reconnect from the AuthorizationCache. If the cache is also
     * empty, fall back to interactive `login()` (which surfaces the three-stage
     * Solana Mobile LNA flow + wallet popups).
     *
     * Returns the wallet-standard `WalletAccount` to use for sign operations.
     */
    async ensureAuthorized() {
        const wallet = await this.ensureWallet();
        if (wallet.accounts.length === 0) {
            try {
                const connectFeat = getConnectFeature(wallet);
                await connectFeat.connect({ silent: true });
            }
            catch (e) {
                console.warn('[SolanaMobileWallet] silent connect failed:', e === null || e === void 0 ? void 0 : e.message);
            }
        }
        if (wallet.accounts.length === 0) {
            const user = await this.login();
            if (!user)
                throw new Error('MWA not connected');
        }
        return wallet.accounts[0];
    }
    /** Returns the active wallet-standard chain identifier (e.g. 'solana:mainnet'). */
    getChain() {
        return mapChainToWalletStandard(this.cluster);
    }
    /**
     * Dynamically import and unwrap `transact` from the MWA web3js
     * package. The package is externalized in rollup.config.js so this
     * resolves to `lib/cjs/index.native.js` (TurboModule-backed) on
     * Metro and `lib/esm/index.browser.js` (WebSocket) on webpack/vite
     * per their respective platform conditions. Handles Metro's CJS-to-
     * ESM interop variance (`mod.transact` vs `mod.default?.transact`).
     */
    async loadTransact() {
        var _a;
        const mod = await import('@solana-mobile/mobile-wallet-adapter-protocol-web3js');
        const t = mod.transact || ((_a = mod.default) === null || _a === void 0 ? void 0 : _a.transact);
        if (typeof t !== 'function') {
            throw new Error('MWA transact API not available on this platform');
        }
        return t;
    }
    /**
     * Cache and persist a fresh MWA auth_token + address. Called from
     * `_loginNative` after a successful authorize. Storage failures are
     * non-fatal (next cold-start re-auths).
     */
    persistNativeAuth(token, address) {
        this.nativeAuthToken = token;
        this.nativeAddress = address;
        try {
            getPlatform().storage.setItem(MWA_AUTH_TOKEN_STORAGE_KEY, JSON.stringify({ token, address }));
        }
        catch (_a) {
            // non-fatal — token still in-memory for this process
        }
    }
    /** Clear cached + persisted native auth state. */
    clearNativeAuth() {
        this.nativeAuthToken = null;
        this.nativeAddress = null;
        try {
            getPlatform().storage.removeItem(MWA_AUTH_TOKEN_STORAGE_KEY);
        }
        catch (_a) {
            // ignore
        }
    }
    /**
     * Classify an MWA-native error as a user cancellation. User-cancel
     * shouldn't surface as a red console.error; callers can swallow or
     * show a softer message. MWA spec codes plus the same substring
     * checks the web path uses to catch wallet-specific phrasing.
     */
    isNativeUserCancel(err) {
        var _a;
        if (!err)
            return false;
        const code = err.code;
        if (code === 'ERROR_NOT_SIGNED')
            return true;
        const msg = String((_a = err.message) !== null && _a !== void 0 ? _a : '').toLowerCase();
        return /user (rejected|denied|cancelled|canceled|declined)/.test(msg);
    }
    /**
     * Native MWA login via @solana-mobile/mobile-wallet-adapter-protocol-web3js.
     * Used in true React Native (Expo) environments where wallet-standard-mobile's
     * web universal-link path doesn't apply. Does authorize + signMessage in a
     * single transact() roundtrip — intent-based MWA doesn't need the two-popup
     * dance the web path uses (no Chrome activation-loss concern). Persists the
     * resulting auth_token so cold-start can reauthorize without a fresh popup.
     */
    async _loginNative() {
        const transact = await this.loadTransact();
        const existingSession = await SessionManager.getSession();
        const nonce = await genAuthNonce();
        const result = await transact(async (wallet) => {
            const auth = await wallet.authorize({
                chain: this.getChain(),
                identity: this.appIdentity,
            });
            if (!auth.accounts || auth.accounts.length === 0) {
                throw new Error('MWA returned no accounts');
            }
            const account = auth.accounts[0];
            // account.address is base64-encoded per MWA spec; convert to
            // the base58 form Tarobase + the rest of the SDK use.
            const base58Addr = new PublicKey(Buffer.from(account.address, 'base64')).toBase58();
            // Reuse session if already valid for this address — skip the
            // signMessages roundtrip entirely.
            if (existingSession && existingSession.address === base58Addr) {
                return {
                    base58Addr,
                    authToken: auth.auth_token,
                    signatureBase64: null,
                };
            }
            const messageText = await genSolanaMessage(base58Addr, nonce);
            const messageBytes = getPlatform().textEncode(messageText);
            const signatures = await wallet.signMessages({
                addresses: [account.address],
                payloads: [messageBytes],
            });
            if (!signatures || signatures.length === 0) {
                throw new Error('MWA returned no signature');
            }
            // Per spec, signMessages returns Uint8Array[] of signatures
            // (one per payload), not message+signature concatenations.
            const signatureBase64 = Buffer.from(signatures[0]).toString('base64');
            return {
                base58Addr,
                authToken: auth.auth_token,
                signatureBase64,
                messageText,
            };
        });
        this.persistNativeAuth(result.authToken, result.base58Addr);
        // Existing valid session — short-circuit before re-creating server-side.
        if (existingSession && existingSession.address === result.base58Addr) {
            const user = { provider: this, address: result.base58Addr };
            setCurrentUser(user);
            return user;
        }
        // Create Tarobase session on the server.
        if (!result.signatureBase64 || !result.messageText) {
            throw new Error('MWA login completed without signature');
        }
        const createSessionResult = await createSessionWithSignature(result.base58Addr, result.messageText, result.signatureBase64);
        await SessionManager.storeSession(result.base58Addr, createSessionResult.accessToken, createSessionResult.idToken, createSessionResult.refreshToken);
        const user = { provider: this, address: result.base58Addr };
        setCurrentUser(user);
        return user;
    }
    async login() {
        var _a, _b, _c, _d, _e;
        setAuthLoading(true);
        // Mark the auth method early so a concurrent Phantom auto-create-session
        // effect (see phantom-wallet-provider) sees 'mobile-wallet-adapter'
        // during our slow connect/sign roundtrip and backs off. Capture the
        // previous value so we can restore it if login fails.
        const prevAuthMethod = readAuthMethod();
        writeAuthMethod(MWA_AUTH_METHOD);
        try {
            // True React Native: skip the wallet-standard-mobile path (which
            // requires window.isSecureContext and a real DOM) and use the raw
            // MWA protocol via Android intents.
            if (!getPlatform().hasDOM) {
                const user = await this._loginNative();
                return user;
            }
            const wallet = await this.ensureWallet();
            // Quick-check: wallet may already have authorization (e.g. from a
            // previous in-page login) and a matching Tarobase session — skip
            // popups in that case.
            if (wallet.accounts.length > 0) {
                const accountPubkey = new PublicKey(wallet.accounts[0].publicKey);
                const base58Addr = accountPubkey.toBase58();
                const existingSession = await SessionManager.getSession();
                if (existingSession && existingSession.address === base58Addr) {
                    const user = { provider: this, address: base58Addr };
                    setCurrentUser(user);
                    return user;
                }
            }
            // Wallet popup #1: standard:connect performs MWA authorize. This
            // is where wallet-standard's checkLocalNetworkAccessPermission()
            // fires the three-stage LNA UX (info modal → permission prompt →
            // success modal) before opening the localhost WebSocket.
            //
            // Wrap with single-retry: on a Seeker origin's first MWA usage,
            // the protocol's session-establishment timer often races Seeker's
            // first-time consent UI and throws ERROR_WALLET_NOT_FOUND before
            // the user finishes tapping. The retry hits Seeker's cached
            // consent on the second pass and completes quickly. See
            // withMwaAssociationRetry for details.
            const connectFeat = getConnectFeature(wallet);
            const { accounts } = await withMwaAssociationRetry(() => connectFeat.connect());
            if (!accounts || accounts.length === 0) {
                throw new Error('MWA returned no accounts');
            }
            const account = accounts[0];
            const accountPubkey = new PublicKey(account.publicKey);
            const base58Addr = accountPubkey.toBase58();
            // If we happen to already have a matching Tarobase session, reuse it.
            const existingSession = await SessionManager.getSession();
            if (existingSession && existingSession.address === base58Addr) {
                const user = { provider: this, address: base58Addr };
                setCurrentUser(user);
                return user;
            }
            // Wallet popup #2: sign the Tarobase nonce message.
            //
            // We cannot call signMessageFeat.signMessage() directly here:
            // it would run as a JS continuation from the connect() resolve,
            // with no fresh Chrome transient user activation. The wallet-
            // standard sign path internally dispatches a new solana-wallet:
            // Android intent via window.location.assign() (see
            // @solana-mobile/mobile-wallet-adapter-protocol launchAssociation
            // at lib/cjs/index.browser.js:459). Chrome blocks the assign
            // without activation, the blur event never fires, and the
            // protocol's 3-second getDetectionPromise rejects with
            // ERROR_WALLET_NOT_FOUND.
            //
            // Insert a Tarobase modal: the user's tap on "Sign in" provides a
            // fresh activation, and signMessage is invoked synchronously
            // inside the click handler so the activation propagates.
            // C1: fetch the nonce HERE (after connect), not before — a pre-connect
            // network round-trip was decaying the Chrome user activation needed for
            // the connect's solana-wallet: navigation, intermittently causing
            // ERROR_WALLET_NOT_FOUND. The nonce is only needed for the message below.
            const nonce = await genAuthNonce();
            const messageText = await genSolanaMessage(base58Addr, nonce);
            const messageBytes = getPlatform().textEncode(messageText);
            const signMessageFeat = getSignMessageFeature(wallet);
            const signResult = await awaitSignInGestureAndSign(this.config.theme, () => signMessageFeat.signMessage({ account, message: messageBytes }));
            if (!signResult) {
                throw new Error('MWA returned no signature');
            }
            const { signature: sigBytes } = signResult;
            const signatureBase64 = Buffer.from(sigBytes).toString('base64');
            // Create Tarobase session on the server.
            const createSessionResult = await createSessionWithSignature(base58Addr, messageText, signatureBase64);
            await SessionManager.storeSession(base58Addr, createSessionResult.accessToken, createSessionResult.idToken, createSessionResult.refreshToken);
            // Auth-method marker is already 'mobile-wallet-adapter' from above.
            const user = { provider: this, address: base58Addr };
            setCurrentUser(user);
            return user;
        }
        catch (error) {
            // Restore the previous auth-method marker since this login attempt failed.
            writeAuthMethod(prevAuthMethod);
            const isUserRejection = (error === null || error === void 0 ? void 0 : error.code) === 4001 ||
                ((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes('user rejected')) ||
                ((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes('user denied')) ||
                ((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes('user cancelled')) ||
                ((_d = error === null || error === void 0 ? void 0 : error.message) === null || _d === void 0 ? void 0 : _d.toLowerCase().includes('user canceled')) ||
                ((_e = error === null || error === void 0 ? void 0 : error.message) === null || _e === void 0 ? void 0 : _e.toLowerCase().includes('user declined'));
            if (!isUserRejection) {
                console.error('[SolanaMobileWallet] Login failed:', error);
            }
            throw error;
        }
        finally {
            setAuthLoading(false);
        }
    }
    async restoreSession() {
        const session = await SessionManager.getSession();
        if (session) {
            return { provider: this, address: session.address };
        }
        return null;
    }
    async logout() {
        if (!getPlatform().hasDOM) {
            // Native path: deauthorize the cached MWA auth_token if we have
            // one (failures non-fatal — the wallet app may have been
            // uninstalled or the token already revoked), then clear local
            // and persisted state.
            const token = this.nativeAuthToken;
            if (token) {
                try {
                    const transact = await this.loadTransact();
                    await transact(async (wallet) => {
                        await wallet.deauthorize({ auth_token: token });
                    });
                }
                catch (error) {
                    console.warn('[SolanaMobileWallet] Native deauthorize error:', error);
                }
            }
            this.clearNativeAuth();
            SessionManager.clearSession();
            writeAuthMethod(null);
            setCurrentUser(null);
            return;
        }
        try {
            const wallet = await this.ensureWallet();
            const disconnectFeat = getDisconnectFeature(wallet);
            if (disconnectFeat) {
                // Disconnect clears the AuthorizationCache and resets
                // wallet.#authorization internally (no LNA dialog).
                await disconnectFeat.disconnect();
            }
        }
        catch (error) {
            console.error('[SolanaMobileWallet] Disconnect error:', error);
        }
        SessionManager.clearSession();
        // Clear the auth-method marker so Phantom auto-create is unblocked
        // for any subsequent fresh login on this device.
        writeAuthMethod(null);
        setCurrentUser(null);
    }
    async signMessage(message) {
        var _a, _b, _c;
        // Native: reauthorize with cached auth_token, then signMessages inside
        // the same transact() session. No wallet-pick popup if the auth_token
        // is still valid for the user's wallet.
        if (!getPlatform().hasDOM) {
            if (!this.nativeAuthToken || !this.nativeAddress) {
                throw new Error('Wallet not connected. Call login() first.');
            }
            const transact = await this.loadTransact();
            const messageBytes = getPlatform().textEncode(message);
            const addressBase64 = new PublicKey(this.nativeAddress).toBuffer().toString('base64');
            const authToken = this.nativeAuthToken;
            try {
                const signature = await transact(async (wallet) => {
                    await wallet.reauthorize({ auth_token: authToken, identity: this.appIdentity });
                    const sigs = await wallet.signMessages({
                        addresses: [addressBase64],
                        payloads: [messageBytes],
                    });
                    if (!sigs || sigs.length === 0)
                        throw new Error('MWA returned no signature');
                    return sigs[0];
                });
                return Buffer.from(signature).toString('base64');
            }
            catch (error) {
                if (this.isNativeUserCancel(error)) {
                    throw new Error('User declined to sign');
                }
                if ((error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED' || (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_REAUTHORIZE') {
                    await this.logout();
                    throw new Error('Wallet connection lost. Please reconnect.');
                }
                throw new Error(`Failed to sign message: ${(_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : error}`);
            }
        }
        const account = await this.ensureAuthorized();
        const wallet = await this.ensureWallet();
        try {
            const signMessageFeat = getSignMessageFeature(wallet);
            const messageBytes = getPlatform().textEncode(message);
            const results = await signMessageFeat.signMessage({ account, message: messageBytes });
            if (!results || results.length === 0) {
                throw new Error('MWA returned no signature');
            }
            const { signature: sigBytes } = results[0];
            return Buffer.from(sigBytes).toString('base64');
        }
        catch (error) {
            if (((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes('not connected')) || ((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.includes('not authorized')) ||
                (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED') {
                await this.logout();
                throw new Error('Wallet connection lost. Please reconnect.');
            }
            throw new Error(`Failed to sign message: ${error.message}`);
        }
    }
    async signTransaction(transaction) {
        var _a, _b, _c;
        // Native: fill blockhash/feePayer if missing, then signTransactions
        // inside a transact() session keyed by the cached auth_token.
        if (!getPlatform().hasDOM) {
            if (!this.nativeAuthToken || !this.nativeAddress) {
                throw new Error('Wallet not connected. Call login() first.');
            }
            const connectedPubkey = new PublicKey(this.nativeAddress);
            const isLegacyTransaction = 'recentBlockhash' in transaction && !('message' in transaction && 'staticAccountKeys' in transaction.message);
            if (isLegacyTransaction) {
                const legacyTx = transaction;
                if (!legacyTx.recentBlockhash) {
                    const conn = new Connection(this.getRpcUrl(), 'confirmed');
                    const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash('confirmed');
                    legacyTx.recentBlockhash = blockhash;
                    legacyTx.lastValidBlockHeight = lastValidBlockHeight;
                }
                if (!legacyTx.feePayer) {
                    legacyTx.feePayer = connectedPubkey;
                }
            }
            else {
                const versionedTx = transaction;
                if (!versionedTx.message.recentBlockhash) {
                    const conn = new Connection(this.getRpcUrl(), 'confirmed');
                    const { blockhash } = await conn.getLatestBlockhash('confirmed');
                    versionedTx.message.recentBlockhash = blockhash;
                }
            }
            const transact = await this.loadTransact();
            const authToken = this.nativeAuthToken;
            try {
                const signed = await transact(async (wallet) => {
                    await wallet.reauthorize({ auth_token: authToken, identity: this.appIdentity });
                    const results = await wallet.signTransactions({ transactions: [transaction] });
                    if (!results || results.length === 0)
                        throw new Error('MWA returned no signed transaction');
                    return results[0];
                });
                return signed;
            }
            catch (error) {
                if (this.isNativeUserCancel(error)) {
                    throw new Error('User declined to sign');
                }
                if ((error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED' || (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_REAUTHORIZE') {
                    await this.logout();
                    throw new Error('Wallet connection lost. Please reconnect.');
                }
                throw new Error(`Failed to sign transaction: ${(_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : error}`);
            }
        }
        const account = await this.ensureAuthorized();
        const connectedPubkey = new PublicKey(account.publicKey);
        const wallet = await this.ensureWallet();
        const chain = this.getChain();
        // Preserve existing prep: fill missing blockhash / fee payer so call
        // sites that build a tx without these fields don't regress.
        const isLegacyTransaction = 'recentBlockhash' in transaction && !('message' in transaction && 'staticAccountKeys' in transaction.message);
        if (isLegacyTransaction) {
            const legacyTx = transaction;
            if (!legacyTx.recentBlockhash) {
                const conn = new Connection(this.getRpcUrl(), 'confirmed');
                const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash('confirmed');
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
            }
            if (!legacyTx.feePayer) {
                legacyTx.feePayer = connectedPubkey;
            }
        }
        else {
            const versionedTx = transaction;
            if (!versionedTx.message.recentBlockhash) {
                const conn = new Connection(this.getRpcUrl(), 'confirmed');
                const { blockhash } = await conn.getLatestBlockhash('confirmed');
                versionedTx.message.recentBlockhash = blockhash;
            }
        }
        try {
            const signTxFeat = getSignTransactionFeature(wallet);
            const wireBytes = txToWireBytes(transaction);
            // signTransaction navigates to `solana-wallet:`; only show the
            // gesture modal if the tap activation has decayed (see runTransaction).
            const signResult = await signWithFreshActivation(this.config.theme, () => signTxFeat.signTransaction({ account, transaction: wireBytes, chain }), 'MWA returned no signed transaction');
            const { signedTransaction: signedBytes } = signResult;
            return txFromWireBytes(new Uint8Array(signedBytes));
        }
        catch (error) {
            if (((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes('not connected')) || ((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.includes('not authorized')) ||
                (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED') {
                await this.logout();
                throw new Error('Wallet connection lost. Please reconnect.');
            }
            throw new Error(`Failed to sign transaction: ${error.message}`);
        }
    }
    async signAndSubmitTransaction(transaction, feePayer) {
        var _a, _b, _c, _d, _e, _f, _g;
        // Native: signAndSendTransactions inside a transact() session.
        // The wallet submits to its own RPC; we then await confirmation on
        // ours. Surfnet (custom RPC) needs manual submission because the
        // wallet won't route there — we fall back to sign-only + manual send.
        if (!getPlatform().hasDOM) {
            if (!this.nativeAuthToken || !this.nativeAddress) {
                throw new Error('Wallet not connected. Call login() first.');
            }
            const connectedPubkey = new PublicKey(this.nativeAddress);
            const rpcUrl = this.getRpcUrl();
            const connection = new Connection(rpcUrl, 'confirmed');
            const isSurfnet = rpcUrl === SURFNET_RPC_URL;
            const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
            const isLegacyTransaction = 'recentBlockhash' in transaction && !('message' in transaction && 'staticAccountKeys' in transaction.message);
            if (isLegacyTransaction) {
                const legacyTx = transaction;
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
                if (!legacyTx.feePayer)
                    legacyTx.feePayer = feePayer !== null && feePayer !== void 0 ? feePayer : connectedPubkey;
            }
            else {
                const versionedTx = transaction;
                versionedTx.message.recentBlockhash = blockhash;
            }
            const transact = await this.loadTransact();
            const authToken = this.nativeAuthToken;
            try {
                if (isSurfnet) {
                    // Surfnet: sign only, submit manually to Surfnet RPC
                    // (the wallet would route signAndSend to its own RPC).
                    const signed = await transact(async (wallet) => {
                        await wallet.reauthorize({ auth_token: authToken, identity: this.appIdentity });
                        const results = await wallet.signTransactions({ transactions: [transaction] });
                        if (!results || results.length === 0)
                            throw new Error('MWA returned no signed transaction');
                        return results[0];
                    });
                    const sig = await connection.sendRawTransaction(signed.serialize(), { preflightCommitment: 'confirmed' });
                    await confirmAndCheckTransaction(connection, sig);
                    return sig;
                }
                const sig = await transact(async (wallet) => {
                    await wallet.reauthorize({ auth_token: authToken, identity: this.appIdentity });
                    const results = await wallet.signAndSendTransactions({ transactions: [transaction] });
                    if (!results || results.length === 0)
                        throw new Error('MWA returned no signature');
                    return results[0];
                });
                await confirmAndCheckTransaction(connection, sig);
                return sig;
            }
            catch (error) {
                if (this.isNativeUserCancel(error)) {
                    throw new Error('User declined to sign');
                }
                if ((error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED' || (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_REAUTHORIZE') {
                    await this.logout();
                    throw new Error('Wallet connection lost. Please reconnect.');
                }
                throw new Error(`Failed to sign+submit transaction: ${(_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : error}`);
            }
        }
        const account = await this.ensureAuthorized();
        const connectedPubkey = new PublicKey(account.publicKey);
        const wallet = await this.ensureWallet();
        const chain = this.getChain();
        const rpcUrl = this.getRpcUrl();
        const connection = new Connection(rpcUrl, 'confirmed');
        const isSurfnet = rpcUrl === SURFNET_RPC_URL;
        try {
            // Preserve existing prep: refresh blockhash and set fee payer.
            const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
            const isLegacyTransaction = 'recentBlockhash' in transaction && !('message' in transaction && 'staticAccountKeys' in transaction.message);
            if (isLegacyTransaction) {
                const legacyTx = transaction;
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
                if (!legacyTx.feePayer) {
                    legacyTx.feePayer = feePayer !== null && feePayer !== void 0 ? feePayer : connectedPubkey;
                }
            }
            else {
                const versionedTx = transaction;
                versionedTx.message.recentBlockhash = blockhash;
            }
            if (isSurfnet) {
                // Surfnet: sign locally via wallet-standard, submit manually
                // to the Surfnet RPC. Don't route through signAndSendTransaction
                // because the wallet would submit to its own RPC.
                const signTxFeat = getSignTransactionFeature(wallet);
                const wireBytes = txToWireBytes(transaction);
                // Gesture modal only if activation decayed (see runTransaction).
                const signResult = await signWithFreshActivation(this.config.theme, () => signTxFeat.signTransaction({ account, transaction: wireBytes, chain }), 'MWA returned no signed transaction');
                const { signedTransaction: signedBytes } = signResult;
                const signedTx = txFromWireBytes(new Uint8Array(signedBytes));
                const signature = await connection.sendRawTransaction(signedTx.serialize(), {
                    preflightCommitment: 'confirmed',
                });
                const confirmation = await connection.confirmTransaction({
                    signature,
                    blockhash,
                    lastValidBlockHeight,
                }, 'confirmed');
                if (confirmation.value.err) {
                    throw new Error(`Transaction failed: ${confirmation.value.err.toString()}`);
                }
                return signature;
            }
            // Non-Surfnet: wallet signs and submits to its own RPC.
            // Gesture modal only if activation decayed (see runTransaction).
            const signSendFeat = getSignAndSendTransactionFeature(wallet);
            const wireBytes = txToWireBytes(transaction);
            const sendResult = await signWithFreshActivation(this.config.theme, () => signSendFeat.signAndSendTransaction({
                account,
                transaction: wireBytes,
                chain,
                options: { commitment: 'confirmed' },
            }));
            const { signature: sigBytes } = sendResult;
            const signature = bs58.encode(sigBytes);
            await confirmAndCheckTransaction(connection, signature);
            return signature;
        }
        catch (error) {
            if (((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes('not connected')) || ((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.includes('not authorized')) ||
                (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED') {
                await this.logout();
                throw new Error('Wallet connection lost. Please reconnect.');
            }
            const isUserRejection = (error === null || error === void 0 ? void 0 : error.code) === 4001 ||
                ((_d = error === null || error === void 0 ? void 0 : error.message) === null || _d === void 0 ? void 0 : _d.toLowerCase().includes('user rejected')) ||
                ((_e = error === null || error === void 0 ? void 0 : error.message) === null || _e === void 0 ? void 0 : _e.toLowerCase().includes('user denied')) ||
                ((_f = error === null || error === void 0 ? void 0 : error.message) === null || _f === void 0 ? void 0 : _f.toLowerCase().includes('user cancelled')) ||
                ((_g = error === null || error === void 0 ? void 0 : error.message) === null || _g === void 0 ? void 0 : _g.toLowerCase().includes('user canceled'));
            if (!isUserRejection) {
                console.error('[SolanaMobileWallet] Transaction failed:', error);
            }
            throw new Error(`Failed to execute transaction: ${error.message}`);
        }
    }
    async runTransaction(_evmTransactionData, solTransactionData, options) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!solTransactionData) {
            throw new Error('Solana transaction data is required for mobile wallet');
        }
        const account = await this.ensureAuthorized();
        const connectedPubkey = new PublicKey(account.publicKey);
        const wallet = await this.ensureWallet();
        const chain = this.getChain();
        const rpcUrl = this.getRpcUrl(solTransactionData.network);
        const connection = new Connection(rpcUrl, 'confirmed');
        const isSurfnet = rpcUrl === SURFNET_RPC_URL;
        try {
            const remainingAccounts = convertRemainingAccounts(solTransactionData.txArgs[0].remainingAccounts);
            let app_id = solTransactionData.appId;
            if (typeof window !== 'undefined' && window.CUSTOM_TAROBASE_APP_ID_HEADER) {
                app_id = window.CUSTOM_TAROBASE_APP_ID_HEADER;
            }
            if (!app_id) {
                throw new Error('App ID is required');
            }
            // Mock wallet adapter for Anchor — only publicKey is read during build.
            const mockWalletAdapter = {
                publicKey: connectedPubkey,
                signTransaction: async (tx) => tx,
                signAllTransactions: async (txs) => txs,
            };
            const anchorProvider = new anchor.AnchorProvider(connection, mockWalletAdapter, anchor.AnchorProvider.defaultOptions());
            const finalDeduped = [];
            for (const acc of remainingAccounts) {
                const existing = finalDeduped.find((d) => d.pubkey.equals(acc.pubkey));
                if (existing) {
                    existing.isSigner = existing.isSigner || acc.isSigner;
                    existing.isWritable = existing.isWritable || acc.isWritable;
                }
                else {
                    finalDeduped.push(acc);
                }
            }
            let tx;
            if (solTransactionData.signedTransaction) {
                // Server has co-signed a CPI attestation. Do NOT mutate its
                // blockhash — that would invalidate the server's signature.
                tx = VersionedTransaction.deserialize(Buffer.from(solTransactionData.signedTransaction, 'base64'));
            }
            else {
                const result = await buildSetDocumentsTransaction(connection, solTransactionData.txArgs[0].idl, anchorProvider, connectedPubkey, {
                    app_id,
                    documents: solTransactionData.txArgs[0].setDocumentData,
                    delete_paths: solTransactionData.txArgs[0].deletePaths,
                    txData: solTransactionData.txArgs[0].txData
                }, finalDeduped, solTransactionData.lutKey, solTransactionData.preInstructions, false, solTransactionData.additionalLutAddresses);
                tx = result.tx;
            }
            // Sign-only branch — sign the tx but don't submit.
            if ((options === null || options === void 0 ? void 0 : options.shouldSubmitTx) === false) {
                const signTxFeat = getSignTransactionFeature(wallet);
                const wireBytes = txToWireBytes(tx);
                // signTransaction also navigates to `solana-wallet:`; only
                // surface the gesture modal if the tap activation has decayed.
                const signOnlyResult = await signWithFreshActivation(this.config.theme, () => signTxFeat.signTransaction({ account, transaction: wireBytes, chain }), 'MWA returned no signed transaction');
                if (!signOnlyResult) {
                    throw new Error('MWA returned no signed transaction');
                }
                const { signedTransaction: signedBytes } = signOnlyResult;
                const signedTx = txFromWireBytes(new Uint8Array(signedBytes));
                return {
                    signedTransaction: signedTx,
                    blockNumber: 0,
                    gasUsed: '0',
                    data: ''
                };
            }
            if (isSurfnet) {
                // Surfnet: sign locally via wallet-standard, submit manually.
                const signTxFeat = getSignTransactionFeature(wallet);
                const wireBytes = txToWireBytes(tx);
                // signTransaction also navigates to `solana-wallet:`; only
                // surface the gesture modal if the tap activation has decayed.
                const surfnetResult = await signWithFreshActivation(this.config.theme, () => signTxFeat.signTransaction({ account, transaction: wireBytes, chain }), 'MWA returned no signed transaction');
                if (!surfnetResult) {
                    throw new Error('MWA returned no signed transaction');
                }
                const { signedTransaction: signedBytes } = surfnetResult;
                const signedTx = txFromWireBytes(new Uint8Array(signedBytes));
                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
                const signature = await connection.sendRawTransaction(signedTx.serialize(), {
                    preflightCommitment: 'confirmed',
                });
                const confirmation = await connection.confirmTransaction({
                    signature,
                    blockhash,
                    lastValidBlockHeight,
                }, 'confirmed');
                if (confirmation.value.err) {
                    throw new Error(`Transaction failed: ${confirmation.value.err.toString()}`);
                }
                const txInfo = await connection.getParsedTransaction(signature, {
                    maxSupportedTransactionVersion: 0,
                    commitment: 'confirmed',
                });
                return {
                    transactionSignature: signature,
                    blockNumber: (txInfo === null || txInfo === void 0 ? void 0 : txInfo.slot) || 0,
                    gasUsed: ((_a = txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta) === null || _a === void 0 ? void 0 : _a.fee.toString()) || '0',
                    data: txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta,
                };
            }
            // Non-Surfnet: wallet signs and submits to its own RPC.
            // signAndSendTransaction dispatches a `solana-wallet:` navigation
            // that Chrome blocks without transient user activation. Fast txs
            // still hold the tap activation and navigate directly; slow ones
            // (e.g. a swap whose server build exceeds Chrome's ~5s window)
            // have lost it, so signWithFreshActivation shows the "Sign in"
            // modal to mint a fresh activation — no modal when not needed.
            const signSendFeat = getSignAndSendTransactionFeature(wallet);
            const wireBytes = txToWireBytes(tx);
            const sendResult = await signWithFreshActivation(this.config.theme, () => signSendFeat.signAndSendTransaction({
                account,
                transaction: wireBytes,
                chain,
                options: { commitment: 'confirmed' },
            }));
            if (!sendResult) {
                throw new Error('MWA returned no signature');
            }
            const { signature: sigBytes } = sendResult;
            const signature = bs58.encode(sigBytes);
            const txInfo = await confirmAndCheckTransaction(connection, signature);
            return {
                transactionSignature: signature,
                blockNumber: (txInfo === null || txInfo === void 0 ? void 0 : txInfo.slot) || 0,
                gasUsed: ((_b = txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta) === null || _b === void 0 ? void 0 : _b.fee.toString()) || '0',
                data: txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta,
            };
        }
        catch (error) {
            if (((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.includes('not connected')) || ((_d = error === null || error === void 0 ? void 0 : error.message) === null || _d === void 0 ? void 0 : _d.includes('not authorized')) ||
                (error === null || error === void 0 ? void 0 : error.code) === 'ERROR_AUTHORIZATION_FAILED') {
                await this.logout();
                throw new Error('Wallet connection lost. Please reconnect.');
            }
            const isUserRejection = (error === null || error === void 0 ? void 0 : error.code) === 4001 ||
                ((_e = error === null || error === void 0 ? void 0 : error.message) === null || _e === void 0 ? void 0 : _e.toLowerCase().includes('user rejected')) ||
                ((_f = error === null || error === void 0 ? void 0 : error.message) === null || _f === void 0 ? void 0 : _f.toLowerCase().includes('user denied')) ||
                ((_g = error === null || error === void 0 ? void 0 : error.message) === null || _g === void 0 ? void 0 : _g.toLowerCase().includes('user cancelled')) ||
                ((_h = error === null || error === void 0 ? void 0 : error.message) === null || _h === void 0 ? void 0 : _h.toLowerCase().includes('user canceled'));
            if (!isUserRejection) {
                console.error('[SolanaMobileWallet] Transaction failed:', error);
            }
            throw new Error(`Failed to execute transaction: ${error.message}`);
        }
    }
    async getNativeMethods() {
        var _a, _b, _c;
        // Synchronous-ish state read; tolerate the wallet being unauthorized
        // (return nulls). Don't trigger ensureAuthorized() here — callers that
        // need the wallet active should sign through one of the sign methods.
        const wallet = this.wallet;
        if (!wallet) {
            return {
                authToken: null,
                walletUriBase: null,
                publicKey: null,
                base64Address: null,
                wallet: null,
                currentAuthorization: null,
            };
        }
        const acct = (_a = wallet.accounts) === null || _a === void 0 ? void 0 : _a[0];
        const auth = wallet.currentAuthorization;
        let publicKey = null;
        let base64Address = null;
        if (acct) {
            publicKey = new PublicKey(acct.publicKey);
            base64Address = btoa(String.fromCharCode(...acct.publicKey));
        }
        return {
            authToken: (_b = auth === null || auth === void 0 ? void 0 : auth.auth_token) !== null && _b !== void 0 ? _b : null,
            walletUriBase: (_c = auth === null || auth === void 0 ? void 0 : auth.wallet_uri_base) !== null && _c !== void 0 ? _c : null,
            publicKey,
            base64Address,
            wallet,
            currentAuthorization: auth !== null && auth !== void 0 ? auth : null,
        };
    }
    /* ----------------------------------------------------------- *
     * Private Helpers
     * ----------------------------------------------------------- */
    getRpcUrl(network) {
        if (this.networkUrl) {
            return this.networkUrl;
        }
        if (network === 'solana_devnet') {
            return SOLANA_DEVNET_RPC_URL;
        }
        else if (network === 'solana_mainnet') {
            return SOLANA_MAINNET_RPC_URL;
        }
        else if (network === 'surfnet') {
            return SURFNET_RPC_URL;
        }
        return SOLANA_MAINNET_RPC_URL;
    }
}
SolanaMobileWalletProvider.instance = null;
